//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React from 'react';
import { useTranslation } from 'react-i18next';
import DefineLoop from './DefineLoop/DefineLoop';
import SuccessMsg from './Success Message/SuccessMessage';
import DefineLoopImg from '../../icons/modern/team_illustrated.png';
import LoopTeam from '../../icons/LoopTeam.svg';
import SuccessMessageIcon from '../../icons/successMessageIcon.svg';
import Teamspaceimg from '../../icons/modern/team_2.png';
import InviteMember from './InviteMember/InviteMember';
import { RawUser } from './Landing';
import { PollTeamspaceTask } from '../Sidebar/SidebarTypes';

interface NonMember {
  email: string;
}

type LoopStatus = 'success' | 'fail' | 'waiting';
type CreationMode = 'loop' | 'teamspace';
interface StepsDataParams {
  mode: CreationMode;
  fetchedUsers: RawUser[];
  loopName: string;
  setLoopName: (name: string) => void;
  setProgress: (progress: number) => void;
  description: string;
  setDescription: (description: string) => void;
  isSuccess: LoopStatus;
  isSubmitted: boolean;
  setIsSubmitted: (submitted: boolean) => void;
  setSelectedMembers: (selectedMembers: RawUser[]) => void;
  setNonMemberUsers: (nonMembers: NonMember[]) => void;
  teamspaceId: string;
  defineError: string;
  errMsgSuccPage: string;
  setErrMsgSuccPage: (errMsg: string) => void;
  createdLoopId: string;
  updateTeamspaceTask: PollTeamspaceTask;
  setTeamspaceCreationFailed: (failed: boolean) => void;
  setLoopCreationFailed: (failed: boolean) => void;
  failedTask: string;
  incompleteTask: string;
  handleNextStep: () => void;
  taskProgressVersion: number;
  taskProgressIndex: number;
  setTaskProgressIndex: (index: number) => void;
  currentDescriptionLoop: string[];
  setCurrentDescriptionLoop: React.Dispatch<React.SetStateAction<string[]>>;
  warning: string;
  setWarning: (warning: string) => void;
  executeLoopTasks: boolean;
  demoDataType: string;
  setdemoDataType: (type: string) => void;
  importDemoData: boolean;
  setImportDemoData: (importDemoData: boolean) => void;
  selectedColor: string;
  setSelectedColor: (color: string) => void;
}

export const useStepsData = ({
  mode,
  fetchedUsers,
  loopName,
  setLoopName,
  setProgress,
  description,
  setDescription,
  isSuccess,
  isSubmitted,
  setIsSubmitted,
  setSelectedMembers,
  setNonMemberUsers,
  teamspaceId,
  defineError,
  errMsgSuccPage,
  setErrMsgSuccPage,
  createdLoopId,
  updateTeamspaceTask,
  setTeamspaceCreationFailed,
  setLoopCreationFailed,
  failedTask,
  incompleteTask,
  handleNextStep,
  taskProgressVersion,
  taskProgressIndex,
  setTaskProgressIndex,
  currentDescriptionLoop,
  setCurrentDescriptionLoop,
  warning,
  setWarning,
  executeLoopTasks,
  demoDataType,
  setdemoDataType,
  importDemoData,
  setImportDemoData,
  selectedColor,
  setSelectedColor,
}: StepsDataParams) => {
  const { t } = useTranslation();
  const taskDescriptions: Record<string, string[]> = {
    PLAN: [
      t('plan').toUpperCase() + ': ' + loopName + ' ' + t('teamspace'),
      t('teamspace-plan'),
    ],
    TEST: [
      t('test').toUpperCase() + ': ' + loopName + ' ' + t('teamspace'),
      t('teamspace-test'),
    ],
    VELOCITY: [
      t('velocity').toUpperCase() + ': ' + loopName + ' ' + t('teamspace'),
      t('teamspace-measure'),
    ],
    CONTROL: [
      t('control').toUpperCase() + ': ' + loopName + ' ' + t('teamspace'),
      t('teamspace-control'),
    ],
    DEPLOY: [
      t('deploy').toUpperCase() + ': ' + loopName + ' ' + t('teamspace'),
      t('teamspace-deploy'),
    ],
    BUILD: [
      t('build').toUpperCase() + ': ' + loopName + ' ' + t('teamspace'),
      t('teamspace-build'),
    ],
  };

  if (mode === 'loop') {
    return {
      currentDescriptionLoop,
      steps: [
        { id: 1, name: t('define-loop') },
        { id: 2, name: t('invite-members') },
        { id: 3, name: t('Create-Loop') },
        { id: 4, name: t('confirmation') },
      ],
      taskDescriptions,
      images: [DefineLoopImg, LoopTeam, SuccessMessageIcon, SuccessMessageIcon],
      pages: [
        <DefineLoop
          key="define-loop"
          mode={mode}
          loopName={loopName}
          setLoopName={setLoopName}
          description={description}
          setDescription={setDescription}
          isSubmitted={isSubmitted}
          defineError={defineError}
          setIsSubmitted={setIsSubmitted}
          onNextStep={handleNextStep}
          warning={warning}
          setWarning={setWarning}
          demoDataType={demoDataType}
          setdemoDataType={setdemoDataType}
          importDemoData={importDemoData}
          setImportDemoData={setImportDemoData}
          selectedColor={selectedColor}
          setSelectedColor={setSelectedColor}
        />,
        <InviteMember
          key="loop-invite"
          members={fetchedUsers}
          onMembersSelected={setSelectedMembers}
          setNonMemberUsers={setNonMemberUsers}
          mode={mode}
        />,
        <SuccessMsg
          key="success-message"
          loopName={loopName}
          description={description}
          setProgress={setProgress}
          isSuccess={isSuccess}
          mode={mode}
          teamspaceId={teamspaceId}
          errMsgSuccPage={errMsgSuccPage}
          setErrMsgSuccPage={setErrMsgSuccPage}
          loopId={createdLoopId}
          setLoopCreationFailed={setLoopCreationFailed}
          taskProgressVersion={taskProgressVersion}
          taskProgressIndex={taskProgressIndex}
          setTaskProgressIndex={setTaskProgressIndex}
          currentDescriptionLoop={currentDescriptionLoop}
          setCurrentDescriptionLoop={setCurrentDescriptionLoop}
          executeLoopTasks={executeLoopTasks}
          demoDataType={demoDataType}
          setdemoDataType={setdemoDataType}
          importDemoData={importDemoData}
        />,
        null,
      ],
      welcomeMessages: [
        t('loop-creation-start'),
        t('invite-member-loop'),
        t('setting-up-loop'),
        t('loop-successfully-created'),
      ],
      descriptions: [
        [t('manage-workflow'), t('manage-workflow-description')],
        [t('manage-workflow'), t('manage-workflow-description')],
        [t('')],
        [t('')],
      ],
      headings: [
        t('loop-creation-start-heading'),
        t('invite-member-loop'),
        t('setting-up-loop-heading'),
        t('confirmation'),
      ],
    };
  } else {
    return {
      steps: [
        { id: 1, name: t('define-teamspace') },
        { id: 2, name: t('invite-members') },
        { id: 3, name: t('create-teamspace') },
        { id: 4, name: t('confirmation') },
      ],
      taskDescriptions,
      images: [Teamspaceimg, Teamspaceimg, Teamspaceimg, Teamspaceimg],
      pages: [
        <DefineLoop
          key="define-loop"
          mode={mode}
          loopName={loopName}
          setLoopName={setLoopName}
          description={description}
          setDescription={setDescription}
          isSubmitted={isSubmitted}
          defineError={defineError}
          setIsSubmitted={setIsSubmitted}
          onNextStep={handleNextStep}
          warning={warning}
          setWarning={setWarning}
          demoDataType={demoDataType}
          setdemoDataType={setdemoDataType}
          importDemoData={importDemoData}
          setImportDemoData={setImportDemoData}
          selectedColor={selectedColor}
          setSelectedColor={setSelectedColor}
        />,
        <InviteMember
          key="teamspace-invite"
          members={fetchedUsers}
          onMembersSelected={setSelectedMembers}
          setNonMemberUsers={setNonMemberUsers}
          mode={mode}
        />,
        <SuccessMsg
          key="success-message"
          loopName={loopName}
          description={description}
          setProgress={setProgress}
          isSuccess={isSuccess}
          mode={mode}
          teamspaceId={teamspaceId}
          errMsgSuccPage={errMsgSuccPage}
          setErrMsgSuccPage={setErrMsgSuccPage}
          loopId={createdLoopId}
          updateTeamspaceTask={updateTeamspaceTask}
          setTeamspaceCreationFailed={setTeamspaceCreationFailed}
          failedTask={failedTask}
          incompleteTask={incompleteTask}
          taskProgressVersion={taskProgressVersion}
          taskProgressIndex={taskProgressIndex}
          setTaskProgressIndex={setTaskProgressIndex}
          currentDescriptionLoop={currentDescriptionLoop}
          setCurrentDescriptionLoop={setCurrentDescriptionLoop}
          executeLoopTasks={executeLoopTasks}
          demoDataType={demoDataType}
          setdemoDataType={setdemoDataType}
          importDemoData={importDemoData}
        />,
        null,
      ],
      welcomeMessages: [
        t('teamspace-creation-start'),
        t('teamspace-creation-start'),
        t('setting-up-teamspace'),
        t('congratulations'),
      ],
      descriptions: [
        [t('teamspace-intro-description')],
        [t('teamspace-intro-description')],
        [t('takes-time-keep-posted')],
        [t('')],
      ],
      headings: [
        t('teamspace-creation-start-heading'),
        t('invite-member-teamspace'),
        t('setting-up-teamspace-heading'),
        t('confirmation'),
      ],
    };
  }
};
